import { FaIdCard, FaFileInvoiceDollar, FaHome, FaUniversity } from "react-icons/fa";
import { useInView } from "../hooks/useInView";
import { useState } from "react";

const DocumentsRequired = () => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const documents = [
    {
      icon: <FaIdCard />,
      title: "ID Proof",
      description: "Aadhaar Card, PAN Card, Passport, or Voter ID",
      color: "from-[#845EC2] to-[#5B3A8F]",
      bgGradient: "from-[#F3E5FF] to-[#E1BEF5]",
    },
    {
      icon: <FaFileInvoiceDollar />,
      title: "Income Proof",
      description: "Latest salary slips or Form 16",
      color: "from-[#FF6F91] to-[#D63864]",
      bgGradient: "from-[#FFE5EC] to-[#FFC9D9]",
    },
    {
      icon: <FaHome />,
      title: "Address Proof",
      description: "Utility bills, rental agreement, or Aadhaar",
      color: "from-[#FFC75F] to-[#F9A825]",
      bgGradient: "from-[#FFF8E1] to-[#FFECB3]",
    },
    {
      icon: <FaUniversity />,
      title: "Bank Statement",
      description: "Last 3 months bank statement",
      color: "from-[#00D2FC] to-[#0097B2]",
      bgGradient: "from-[#E0F7FF] to-[#B3E5FC]",
    },
  ];

  return (
    <section ref={ref} className="w-full py-16 md:py-20 lg:py-24 bg-gradient-to-br from-white via-[#F8FFFE] to-[#F0FFFE] relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-20 w-64 h-64 bg-[#A9F9EE] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#7DD4D0] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-[#21A6B5] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        {/* Enhanced Header */}
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="inline-block mb-4">
            <span className="px-4 py-1 bg-gradient-to-r from-[#21A6B5] to-[#3D4A7C] text-white text-base leading-6 font-semibold text-center font-['Fredoka'] rounded-full shadow-md">
              📄 Quick & Easy
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-[60px] lg:leading-[60px] font-bold text-center text-[#2C3E50] mb-4 font-['Inter']">
            Documents Required
          </h2>
          <p className="text-lg md:text-xl lg:text-[24px] lg:leading-[32px] font-normal text-center text-gray-600 font-['Inter'] max-w-3xl mx-auto">
            Just a few simple documents to get you started
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {documents.map((item, index) => (
            <div
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`group relative bg-white rounded-3xl px-5 py-7 md:px-6 md:py-8 shadow-xl hover:shadow-2xl transition-all duration-700 cursor-pointer text-center overflow-hidden ${
                isInView ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              } ${
                hoveredIndex === index ? 'scale-105 -translate-y-3' : 'scale-100'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Animated Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${item.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
              
              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-40 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>

              {/* Floating Particles */}
              <div className="absolute inset-0 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute top-5 left-5 w-1.5 h-1.5 bg-white rounded-full animate-float"></div>
                <div className="absolute top-10 right-5 w-2 h-2 bg-white rounded-full animate-float" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute bottom-5 left-1/3 w-1.5 h-1.5 bg-white rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon Container with Gradient */}
                <div className={`w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-3xl md:text-4xl mb-6 mx-auto shadow-xl transform transition-all duration-500 ${
                  hoveredIndex === index ? 'rotate-[360deg] scale-110' : 'rotate-0 scale-100'
                }`}>
                  <div className={`transition-all duration-500 ${
                    hoveredIndex === index ? 'scale-125 animate-pulse' : 'scale-100'
                  }`}>
                    {item.icon}
                  </div>
                </div>

                {/* Title */}
                <h3 className={`text-lg md:text-xl font-bold mb-3 font-['Inter'] transition-all duration-300 ${
                  hoveredIndex === index ? 'text-[#3D4A7C] scale-105' : 'text-[#2C3E50] scale-100'
                }`}>
                  {item.title}
                </h3>

                {/* Description */}
                <p className={`text-gray-700 text-sm md:text-base leading-relaxed font-['Inter'] transition-colors duration-300 ${
                  hoveredIndex === index ? 'text-gray-800' : 'text-gray-700'
                }`}>
                  {item.description}
                </p>

                {/* Animated Border */}
                <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 bg-gradient-to-r ${item.color} transition-all duration-500 rounded-full ${
                  hoveredIndex === index ? 'w-3/4' : 'w-0'
                }`}></div>
              </div>

              {/* Corner Decoration */}
              <div className={`absolute top-0 right-0 w-12 h-12 bg-gradient-to-br ${item.color} opacity-10 rounded-bl-full transition-all duration-500 ${
                hoveredIndex === index ? 'scale-150' : 'scale-100'
              }`}></div>
            </div>
          ))}
        </div>

        {/* Bottom Info */}
        <div className={`text-center mt-10 md:mt-14 transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: '400ms' }}>
          <p className="text-base md:text-lg text-gray-600 font-['Inter']">
            All documents can be uploaded digitally - No physical copies needed! 📱
          </p>
        </div>
      </div>
    </section>
  );
};

export default DocumentsRequired;
